import React from "react";
import { Link } from "react-router-dom";
import MarketingPageLayout, { PolicySection } from "@/components/marketing/MarketingPageLayout";
import { Building2, ShieldCheck, Globe2, Users, Sparkles } from "lucide-react";

/**
 * /about — Company story / mission. Linked from the footer and the Trust Center.
 */
export default function About() {
  return (
    <MarketingPageLayout
      eyebrow="Our story"
      title="Hospitality intelligence, with privacy at its core."
      subtitle="GuestIntel was founded to give independent hotels, resorts, and lodging groups the same operational intelligence that the largest brands have spent two decades building in-house — without compromising guest privacy."
      path="/about"
      seoTitle="About GuestIntel"
      seoDescription="GuestIntel is the privacy-first hospitality intelligence network. Verified incident records, Intel Shield matching, and a fair appeal process — built for independent properties."
    >
      <PolicySection title="Why we exist">
        <p>
          Hospitality businesses absorb billions of dollars in property damage, chargebacks, fraud, and
          safety incidents every year. Most of that loss is preventable — the patterns repeat across
          properties, across cities, across brands. But until now there was no privacy-respecting way for a
          front-desk manager to know that the reservation walking in tonight has documented incidents at
          three other properties.
        </p>
        <p>
          GuestIntel exists to close that gap. We built a verified, evidence-based intelligence network where
          participating properties share incident verdicts (not raw customer data) so the whole industry
          gets safer with every new member.
        </p>
      </PolicySection>

      <PolicySection title="What we believe">
        <ul>
          <li><strong>Privacy is non-negotiable.</strong> We invented Intel Shield™ — a matching engine that returns the verdict, not the customer.</li>
          <li><strong>Evidence over rumour.</strong> Every record is documented with photos, damage estimates, and verified by GuestIntel's review team before it enters the network.</li>
          <li><strong>Fair process.</strong> Guests have a real, fast, free appeal path. We treat appeals as a feature, not a complaint.</li>
          <li><strong>Independent properties first.</strong> Big chains already have this. We're building it for everyone else.</li>
        </ul>
      </PolicySection>

      <PolicySection title="Who we work with">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 not-prose">
          {[
            { icon: Building2, label: "Independent hotels, motels & inns" },
            { icon: Globe2,    label: "Resorts & vacation rentals" },
            { icon: Sparkles,  label: "Boutique groups & brand collections" },
            { icon: Users,     label: "Co-working hospitality & serviced apartments" },
            { icon: ShieldCheck, label: "Compliance & risk teams at multi-property operators" },
          ].map(({ icon: I, label }) => (
            <div key={label} className="border border-zinc-200 rounded-md p-4 flex items-center gap-3">
              <I className="w-5 h-5 text-teal" />
              <span className="text-sm text-zinc-800">{label}</span>
            </div>
          ))}
        </div>
      </PolicySection>

      <PolicySection title="Where we operate">
        <p>
          GuestIntel runs from secure, ISO 27001-aligned data centres with regional residency options
          (US, EU). All cross-border data flows are governed by standard contractual clauses. See our{" "}
          <Link to="/privacy">Privacy Policy</Link> and{" "}
          <Link to="/data-retention">Data Retention Policy</Link> for specifics.
        </p>
      </PolicySection>

      <PolicySection title="How to reach us">
        <p>
          For partnership and press: <a href="mailto:contact@guestintel.net">contact@guestintel.net</a>.<br />
          For trust, security &amp; compliance: <a href="mailto:trust@guestintel.net">trust@guestintel.net</a>.<br />
          For guest enquiries and appeals, please use our public <Link to="/appeal">Appeal Portal</Link>.
        </p>
      </PolicySection>
    </MarketingPageLayout>
  );
}
